import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export interface DropboxConnectionStatus {
  connected: boolean;
  display_name?: string;
  email?: string;
  target_folder?: string;
  last_sync_at?: string;
  last_sync_error?: string;
}

// ============================================================================
// Query Hooks
// ============================================================================

export function useDropboxStatus() {
  return useQuery({
    queryKey: ['dropbox', 'status'],
    queryFn: async () => {
      const response = await api.dropbox.getStatus();
      return response as DropboxConnectionStatus;
    },
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

export function useDropboxConnect() {
  return useMutation({
    mutationFn: () => api.dropbox.connect(),
    onError: (error: any) => {
      toast.error(error.message || 'Failed to start Dropbox connection');
    },
  });
}

export function useDropboxDisconnect() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.dropbox.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dropbox'] });
      toast.success('Dropbox disconnected');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to disconnect Dropbox');
    },
  });
}

export function useUpdateDropboxFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (folder: string) =>
      api.dropbox.updateFolder({ target_folder: folder }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dropbox'] });
      toast.success('Dropbox folder updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update folder');
    },
  });
}

export function useDropboxSyncAll() {
  return useMutation({
    mutationFn: () => api.dropbox.syncAll(),
    onSuccess: (data) => {
      if (data.queued === 0) {
        toast.success('All invoices are already synced to Dropbox');
      } else {
        toast.success(`${data.queued} invoice${data.queued === 1 ? '' : 's'} queued for Dropbox sync`);
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || error?.message || 'Bulk sync failed');
    },
  });
}
